/**
 * AvailabilityPickerDrawer · Phase 2E
 * ─────────────────────────────────────────────────────────────
 * Slide-out drawer that lets an admin/foreman edit a contractor's
 * weekly availability. Two modes:
 *   - "Default recurring" (week_start = null) — used every week unless
 *     a per-week override exists
 *   - Per-week override (week_start = YYYY-MM-DD) — applies to that
 *     Monday-anchored week only
 *
 * Uses shipped endpoints: GET/PUT /api/schedule/availability
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, FloppyDisk, Calendar } from '@phosphor-icons/react';

const DAYS = [
  ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'],
  ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun'],
];
const DEFAULT_DAYS = {
  mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
};

export default function AvailabilityPickerDrawer({ api, contractor, defaultWeekStart, onClose }) {
  const [mode, setMode] = useState('default');  // 'default' | 'week'
  const [weekStart, setWeekStart] = useState(defaultWeekStart);
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setMsg('');
    try {
      const q = mode === 'week'
        ? `contractor_id=${contractor._id}&week_start=${weekStart}`
        : `contractor_id=${contractor._id}`;
      const { data } = await api.get(`/schedule/availability?${q}`);
      const row = (data || []).find(r =>
        (mode === 'week') ? r.week_start === weekStart : !r.week_start
      );
      if (row) {
        setDays({ ...DEFAULT_DAYS, ...(row.days || {}) });
        setNotes(row.notes || '');
      } else {
        setDays(DEFAULT_DAYS);
        setNotes('');
      }
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Load failed');
    }
  }, [api, contractor._id, mode, weekStart]);

  useEffect(() => { load(); }, [load]);

  const toggle = (k) => setDays({ ...days, [k]: !days[k] });

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      await api.put('/schedule/availability', {
        contractor_id: contractor._id,
        week_start: mode === 'week' ? weekStart : null,
        days, notes,
      });
      setMsg('Saved.');
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(
    () => DAYS.filter(([k]) => days[k]).map(([, l]) => l).join(' · ') || '— all off —',
    [days],
  );

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-end"
         onClick={onClose}>
      <div className="w-96 h-full bg-white border-l-2 border-slate-800 p-5 overflow-auto"
           onClick={(e) => e.stopPropagation()}
           data-testid="availability-drawer">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-700 flex items-center gap-2"
              style={{ fontFamily: "'Chivo', sans-serif" }}>
            <Calendar size={16} weight="bold" /> Availability
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100"
                  data-testid="availability-drawer-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="text-xs text-slate-600 mb-4">
          <span className="font-mono text-slate-900 font-bold">{contractor.name}</span>
          <span className="ml-2 text-slate-500">· {contractor.trade || '—'}</span>
        </div>

        {/* Mode toggle */}
        <div className="flex border border-slate-300 mb-4" data-testid="availability-mode">
          <button
            onClick={() => setMode('default')}
            data-testid="availability-mode-default"
            className={`flex-1 text-xs uppercase tracking-wider py-1.5 ${mode === 'default' ? 'bg-slate-900 text-white font-bold' : 'bg-white text-slate-700'}`}>
            Default recurring
          </button>
          <button
            onClick={() => setMode('week')}
            data-testid="availability-mode-week"
            className={`flex-1 text-xs uppercase tracking-wider py-1.5 ${mode === 'week' ? 'bg-slate-900 text-white font-bold' : 'bg-white text-slate-700'}`}>
            Week override
          </button>
        </div>

        {mode === 'week' && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Week starting (Mon)</div>
            <input type="date" value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              data-testid="availability-week-start"
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono" />
          </div>
        )}

        <div className="space-y-2 mb-4" data-testid="availability-days">
          {DAYS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!days[k]} onChange={() => toggle(k)}
                     data-testid={`availability-day-${k}`} />
              <span className="text-sm text-slate-800 flex-1">{l}</span>
              <span className={`text-[10px] font-mono uppercase ${days[k] ? 'text-emerald-700' : 'text-rose-700'}`}>
                {days[k] ? 'working' : 'off'}
              </span>
            </label>
          ))}
        </div>

        <div className="mb-4 text-[11px] font-mono text-slate-600 border-l-4 border-slate-300 px-2 py-1 bg-slate-50">
          Working: {summary}
        </div>

        <label className="block mb-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Notes</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    data-testid="availability-notes"
                    className="w-full border border-slate-300 px-2 py-1 text-sm" />
        </label>

        <button onClick={save} disabled={saving}
                data-testid="availability-save"
                className="w-full bg-slate-900 text-white text-xs uppercase tracking-wider py-2 hover:bg-slate-700 disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <FloppyDisk size={14} weight="bold" />
          {saving ? 'Saving…' : 'Save availability'}
        </button>

        {msg && (
          <div className={`mt-3 text-xs font-mono px-2 py-1 ${msg === 'Saved.' ? 'text-emerald-800 bg-emerald-50 border-l-4 border-emerald-500' : 'text-rose-800 bg-rose-50 border-l-4 border-rose-500'}`}
               data-testid="availability-msg">
            {msg}
          </div>
        )}
      </div>
    </div>
  );
}
